import { useMemo, useState } from "react";
import { leaks, feedTabs } from "@/mocks/home";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "./SectionHeading";

type LatestLeaksProps = {
  activeTag: string | null;
  query: string;
  onSelectTag: (label: string) => void;
  onClearTag: () => void;
};

const sourceStyle: Record<string, string> = {
  独家来源: "bg-primary-500 text-background-50",
  线人投稿: "bg-accent-500 text-foreground-950",
  匿名投稿: "bg-secondary-200 text-secondary-900",
};

export default function LatestLeaks({ activeTag, query, onSelectTag, onClearTag }: LatestLeaksProps) {
  const [tab, setTab] = useState<string>(feedTabs[0]);

  const filtered = useMemo(() => {
    return leaks.filter((item) => {
      const byTab =
        tab === "全部" ||
        (tab === "独家" && item.source === "独家来源") ||
        (tab === "热议" && item.cat === "热议");
      const byTag = !activeTag || item.tags.includes(activeTag);
      const keyword = query.toLowerCase();
      const byQuery =
        !keyword ||
        item.title.toLowerCase().includes(keyword) ||
        item.excerpt.toLowerCase().includes(keyword) ||
        item.tags.join(" ").toLowerCase().includes(keyword);
      return byTab && byTag && byQuery;
    });
  }, [tab, activeTag, query]);

  return (
    <section id="leaks" className="w-full border-b border-background-200 bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-[1440px] px-4 md:px-6">
        <SectionHeading
          icon="ri-flashlight-line"
          eyebrow="最新线报 · LIVE FEED"
          title="刚从投稿箱里过完初筛的那一批"
          desc="下面每一条都带着来源等级、挂载话题与讨论量。图文并列展示，看到感兴趣的可以直接进评论区蹲后续。"
        />

        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
            {feedTabs.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setTab(item)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors ${
                  tab === item
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:text-primary-700"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-600">
              共 <span className="font-medium text-foreground-900">{filtered.length}</span> 条
              {activeTag ? ` · 话题「${activeTag}」` : ""}
              {query ? ` · 关键词「${query}」` : ""}
            </span>
            {(activeTag || query) && (
              <button
                type="button"
                onClick={onClearTag}
                className="flex cursor-pointer items-center gap-1 rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-close-line" />
                清除筛选
              </button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl" />
            </span>
            <p className="mt-4 font-heading text-lg font-bold text-foreground-900">这个筛选下暂时没有线报</p>
            <p className="mt-2 text-sm text-foreground-600">换个话题标签或关键词试试，投稿箱里一直在进来新内容。</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 md:gap-6 lg:grid-cols-2">
            {filtered.map((item, index) => (
              <Reveal key={item.id} delay={index * 60}>
                <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-300 bg-background-50 transition-colors duration-300 hover:border-primary-300">
                  <div className="h-44 w-full overflow-hidden md:h-52">
                    <img
                      src={item.image}
                      alt={`${item.title} 相关线报配图`}
                      title={`${item.title} - 15黑料网 内部爆料`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>

                  <div className="flex flex-1 flex-col p-4 md:p-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-md px-2 py-1 text-[11px] font-label font-medium ${sourceStyle[item.source]}`}>
                        {item.source}
                      </span>
                      {item.tags.slice(0, 2).map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => onSelectTag(tag)}
                          className="cursor-pointer rounded-md bg-secondary-100 px-2 py-1 text-[11px] text-secondary-900 transition-colors hover:bg-secondary-200"
                        >
                          #{tag}
                        </button>
                      ))}
                    </div>

                    <h4 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
                      <a href="#leaks" className="cursor-pointer transition-colors hover:text-primary-700">
                        {item.title}
                      </a>
                    </h4>

                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                      {item.excerpt}
                    </p>

                    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-background-200 pt-4 text-xs text-foreground-600">
                      <span className="flex items-center gap-1.5">
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-user-line" />
                        </span>
                        {item.author}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-time-line" />
                        </span>
                        {item.time}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-eye-line" />
                        </span>
                        {item.views.toLocaleString("en-US")}
                      </span>
                      <span className="ml-auto flex items-center gap-1.5 font-medium text-primary-700">
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-chat-3-line" />
                        </span>
                        {item.comments.toLocaleString("en-US")} 条讨论
                      </span>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}